import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, ChakraProvider, Text } from '@chakra-ui/react';
import { validateCheckoutUrl } from '@common/validation-schemas';
import { useQueryClient } from '@tanstack/react-query';
import { EntrataSession } from 'auth/EntrataSession';
import { resetStoreAction } from 'state/commonActions';
import { setIsAuthenticated } from 'state/slices/authSlice';
import { setLocale } from 'state/slices/localeSlice';
import {
  confirmInternationalPayment,
  setInternationalPaymentId,
  setInternationalPaymentStatus,
} from 'state/slices/paymentSlice';
import { useDispatch } from 'state/storeHooks';

import { Loader } from 'components/Loader';
import { INTERNATIONAL_PAYMENT_STATUS } from 'components/render-page/pages/payment/international/constants';
import {
  executePayment,
  readPaymentInstruction,
} from 'components/render-page/pages/payment/international/paymentIntegrations';
import { persistStripePaymentReturn } from 'components/render-page/pages/payment/international/utils';
import { useSystemTranslation } from 'hooks';
import { useInitializeLJApplication } from 'hooks/useInitializeLJApplication';
import { getApplicationForm } from 'services/application';
import {
  abandonPaymentAttempt,
  bootstrapPaymentAttempt,
  continuePaymentHandoff,
  PaymentAttemptBootstrap,
  startPaymentCheckout,
} from 'services/payment-handoff';
import { getResponse } from 'services/responses';

import { restorePaymentLaunchContext } from './launchContext';
import { PaymentRecoveryView } from './PaymentRecoveryView';

interface Props {
  readonly children: (restored: boolean) => React.ReactNode;
}

/** Only this route continues a previously authorized Finalize operation. */
export function PaymentHandoffBoundary({ children }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const restored = useRef(false);
  const continuationId = location.pathname.match(/^\/\d+\/\d+\/payment-checkout\/([\w-]+)$/)?.[1];
  const isLogin = location.pathname.endsWith('/viewer-login');
  const attemptId = isLogin
    ? null
    : (continuationId ?? new URLSearchParams(location.search).get('paymentAttempt'));
  const [ready, setReady] = useState(!attemptId);
  const [error, setError] = useState<'restore' | 'checkout' | null>(null);
  const [retry, setRetry] = useState(0);
  const [attempt, setAttempt] = useState<PaymentAttemptBootstrap>();
  const [recovering, setRecovering] = useState(false);
  const [busy, setBusy] = useState(false);
  const [paymentError, setPaymentError] = useState<string>();
  const started = useRef<string>();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const initialize = useInitializeLJApplication();
  const { tSystem } = useSystemTranslation();

  useEffect(() => {
    if (!attemptId) return;
    const key = `${attemptId}:${location.search}:${retry}`;
    if (started.current === key) return;
    started.current = key;
    setError(null);
    setRecovering(false);
    setReady(false);
    // Keep the current attempt on an ordinary authentication redirect if the cookie expired.
    EntrataSession.setAppPath(window.location.href);
    async function restore() {
      try {
        const { data } = await bootstrapPaymentAttempt(attemptId!, location.pathname);
        setAttempt(data);
        EntrataSession.setApplicant(data.applicant);
        EntrataSession.setSessionExpired(false);
        restorePaymentLaunchContext(data.launchContext, data.applicant);
        const operation = new URLSearchParams(location.search).get('op');
        if (['verified', 'payment_confirmed'].includes(data.status) && operation !== 'success') {
          window.location.replace(`${data.viewerPath}?paymentAttempt=${data.attemptId}&op=success`);
          return;
        }
        if (
          continuationId &&
          retry === 0 &&
          data.mayContinue !== false &&
          data.status === 'checkout_ready'
        ) {
          const checkout = await startPaymentCheckout(data.attemptId);
          window.location.replace(validateCheckoutUrl(checkout.data.checkoutSessionUrl));
          return;
        }
        const shouldStartCheckout = Boolean(
          continuationId && retry === 0 && data.mayContinue !== false && data.status === 'ready',
        );
        const authenticationOnly = ['handoff_ready', 'handed_off'].includes(data.status);
        if (
          !authenticationOnly &&
          !shouldStartCheckout &&
          (continuationId ||
            ['unknown', 'creating', 'verifying', 'ready', 'awaiting_handoff'].includes(
              data.status,
            ) ||
            (!['success', 'cancel'].includes(operation || '') &&
              !['cancelled', 'failed'].includes(data.status)))
        ) {
          setRecovering(true);
          return;
        }
        queryClient.clear();
        dispatch(resetStoreAction());
        if (data.launchContext.locale) dispatch(setLocale(data.launchContext.locale));
        const application = await getApplicationForm(data.responseId);
        if (!application?.form?.pages?.length) throw new Error('Application unavailable');
        const response = await getResponse(data.responseId);
        if (!response?.data || Object.keys(response.data).length === 0)
          throw new Error('Application response unavailable');
        await initialize({
          applicant: data.applicant,
          form: application.form,
          responseId: data.responseId,
          savedResponse: response.data,
        });
        restored.current = true;
        dispatch(setIsAuthenticated(true));
        if (shouldStartCheckout) {
          // The application and launch context are ready before the server claims Checkout.
          const checkout = await startPaymentCheckout(data.attemptId);
          window.location.replace(validateCheckoutUrl(checkout.data.checkoutSessionUrl));
          return;
        }
        if (authenticationOnly) {
          // Leave the continuation route before calling the same flow as standalone Finalize.
          // Refresh/login now returns to the normal application, never to an automatic payment.
          const destination = new URL(data.viewerPath, window.location.origin).toString();
          EntrataSession.setAppPath(destination);
          if (
            !continuationId ||
            retry > 0 ||
            data.mayContinue === false ||
            data.status !== 'handoff_ready'
          ) {
            if (data.status === 'handoff_ready') await abandonPaymentAttempt(data.attemptId);
            window.location.replace(destination);
            return;
          }
          const { data: intent } = await continuePaymentHandoff(data.attemptId);
          const instruction = readPaymentInstruction(data);
          if (!intent.continuePayment || !instruction) {
            window.location.replace(destination);
            return;
          }
          window.history.replaceState(window.history.state, '', destination);
          try {
            const outcome = await executePayment(instruction, {
              websiteHost: destination,
              translate: tSystem,
              onProcessing: () =>
                dispatch(setInternationalPaymentStatus(INTERNATIONAL_PAYMENT_STATUS.PROCESSING)),
              onPaymentId: (id) => dispatch(setInternationalPaymentId(id)),
            });
            if (outcome === 'completed') {
              dispatch(confirmInternationalPayment(data.responseId));
              setReady(true);
              navigate(data.viewerPath, { replace: true });
            }
          } catch (failure) {
            setPaymentError((failure as Error).message);
          }
          return;
        }
        if (data.paymentId && data.stripePaymentKey) {
          persistStripePaymentReturn({
            paymentId: data.paymentId,
            stripePaymentKey: data.stripePaymentKey,
          });
        }
        setReady(true);
      } catch {
        // Retain the attempt URL. Bootstrap is repeatable even though its handoff code is spent.
        setError(continuationId ? 'checkout' : 'restore');
        // Refresh only local status; never repeat an uncertain Checkout creation.
        if (continuationId)
          try {
            const { data } = await bootstrapPaymentAttempt(attemptId!, location.pathname);
            setAttempt(data);
          } catch {
            /* Authentication/network recovery remains available. */
          }
      }
    }
    restore();
    // StrictMode cannot repeat continuation; the API also atomically claims the one-time intent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptId, continuationId, location.pathname, location.search, retry]);

  async function recover(action: 'continue' | 'abandon') {
    if (!attempt) return;
    setBusy(true);
    try {
      if (action === 'continue') {
        const { data } = await startPaymentCheckout(attempt.attemptId);
        window.location.replace(validateCheckoutUrl(data.checkoutSessionUrl));
      } else {
        const { data } = await abandonPaymentAttempt(attempt.attemptId);
        window.location.replace(`${data.viewerPath}?paymentAttempt=${attempt.attemptId}`);
      }
    } catch {
      setRetry((value) => value + 1);
    } finally {
      setBusy(false);
    }
  }

  if (ready || !attemptId) return <>{children(restored.current || Boolean(attemptId))}</>;
  if (paymentError && attempt)
    return (
      <ChakraProvider>
        <Box minH="100vh" bg="gray.50" px={6} py={24}>
          <Box
            maxW="lg"
            mx="auto"
            bg="white"
            borderWidth="1px"
            borderRadius="lg"
            p={8}
            boxShadow="sm"
            role="alert"
          >
            <Text>{paymentError}</Text>
            <Button mt={6} onClick={() => window.location.replace(attempt.viewerPath)}>
              {tSystem('payment.international.returnToApplication')}
            </Button>
          </Box>
        </Box>
      </ChakraProvider>
    );
  if (error || recovering)
    return (
      <PaymentRecoveryView
        attempt={attempt}
        busy={busy}
        restoreFailed={error === 'restore'}
        onRefresh={() => setRetry((value) => value + 1)}
        onContinue={() => recover('continue')}
        onAbandon={() => recover('abandon')}
      />
    );
  return (
    <ChakraProvider>
      <Loader />
    </ChakraProvider>
  );
}
